import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Heading from 'components/partials/heading/heading';
import Loader from 'components/partials/loader/loader';
import CustomButton from 'components/partials/custom-button/custom-button';
import { CustomSelect as Select } from 'components/partials/custom-select/custom-select';

import CriticalElementsTable from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-table/critical-elements-table';
import useCriticalElements from 'containers/admin/clients/client/tabs-components/critical-elements/hooks/use-critical-elements';
import { generateYearOptions } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-filters/goals-matrix-filters';
import { getCurrentYear } from 'containers/admin/clients/client/client-details';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import Icons from 'assets/icons';

const CriticalElementsTab = ({ clientId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = getCurrentYear();
  const currentUser = useCurrentUser();

  const pathParts = location.pathname.split('/');
  const criticalElementsIndex = pathParts.indexOf('critical-elements');
  const yearFromPath =
    criticalElementsIndex !== -1 && pathParts.length > criticalElementsIndex + 1
      ? pathParts[criticalElementsIndex + 1]
      : null;

  const [selectedYear, setSelectedYear] = useState(
    yearFromPath && !isNaN(parseInt(yearFromPath))
      ? parseInt(yearFromPath)
      : currentYear
  );

  const { criticalElements, loading } = useCriticalElements(
    clientId,
    selectedYear
  );

  useEffect(() => {
    if (location.pathname.endsWith('/critical-elements')) {
      navigate(`/admin/clients/${clientId}/critical-elements/${selectedYear}`, {
        replace: true,
      });
    }
  }, [location.pathname, clientId, navigate, selectedYear]);

  const handleViewDetails = (id) => {
    navigate(
      `/admin/clients/${clientId}/critical-elements/${selectedYear}/${id}`
    );
  };

  const handleAddNew = () => {
    navigate(
      `/admin/clients/${clientId}/critical-elements/${selectedYear}/new`
    );
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    navigate(`/admin/clients/${clientId}/critical-elements/${year}`, {
      replace: true,
    });
  };

  const years = generateYearOptions({ futureYears: 3, pastYears: 2 });

  return (
    <div className="relative">
      <div className="flex justify-between items-center my-5">
        <div className="flex items-center gap-4">
          <Heading variant="h2">Critical Elements</Heading>
          <Select
            options={years}
            value={years.find((year) => year.value === selectedYear)}
            onChange={(option) => handleYearChange(option.value)}
            customSelectWrapperClass="!w-28"
            extraBasicStyles={{ padding: 0, height: '2' }}
          />
        </div>
        {currentUser.data?.admin && (
          <CustomButton
            variant="primary"
            onClick={handleAddNew}
            size="lg"
            iconBefore={<Icons.Plus alt="PlusIcon" width="14" />}
          >
            Add New
          </CustomButton>
        )}
      </div>

      <div className="relative">
        {loading && (
          <Loader loading={true} customClass="!bg-white" fullScreen={false} />
        )}

        <CriticalElementsTable
          criticalElements={criticalElements}
          loading={loading}
          handleViewDetails={handleViewDetails}
          clientId={clientId}
          selectedYear={selectedYear}
        />
      </div>
    </div>
  );
};

export default CriticalElementsTab;
